import constants, { EnrichmentType } from '../constants';
import { actionEventDynamicProperty, getElementId, hasLockedParent } from '../../helpers';
import { Api } from '../../../../../typings/api';

interface EnrichmentParams {
	params: {
		param: string;
		assetDriveItem: Api.AspDriveResourceItemLight;
	};
}

interface EnrichmentData {
	type: EnrichmentType;
	resource: Api.AspDriveResourceItemLight;
}

export interface EnrichmentConfig extends EnrichmentData {
	id: string;
	page: number;
	defaultResource: Api.AspDriveResourceItemLight;
	isEditedFromBackoffice: boolean;
	importManagerId: string;
	isThemedItem: boolean;
}

interface EnrichmentItemsData {
	items: EnrichmentConfig[];
	isError: boolean;
}

export enum EnrichmentElementDataAttribute {
	edited = 'data-edited', // is set to element when it was modified in backoffice
	defaultResource = 'data-default-resource', // is set to element to store default enrichment resource data
	deps = 'data-deps', // is set to element to allow a server to add a resource to publication dependencies
}

export const getEnrichmentData = (element: HTMLElement): EnrichmentData & EnrichmentParams | null => {
	const enrichmentDataString = element.getAttribute('onclick');

	if (!enrichmentDataString) {
		return null;
	}

	const enrichmentRegExp = /javascript:document\.dispatchEvent\(new CustomEvent\("WebPublicationAction", {"detail":([\s\S]*?)}\)\); return false;/;
	const dataMatch = enrichmentDataString.match(enrichmentRegExp);

	if (!dataMatch) {
		return null;
	}

	try {
		const {
			type,
			params,
		} = JSON.parse(
			dataMatch[1]
				.replace(actionEventDynamicProperty, '')
				.replace(/'/g, '"')
				.trim()
		);

		return {
			type,
			resource: params.assetDriveItem,
			params,
		};
	} catch (err) {
		return null;
	}
};

const getConfig = (element: HTMLElement, page: number, type: EnrichmentType, resource: Api.AspDriveResourceItemLight, isAccountManagerOrAdmin: boolean): EnrichmentConfig => {
	const id = getElementId(element);
	const isEditedFromBackoffice = element.getAttribute(EnrichmentElementDataAttribute.edited) === 'true';
	const defaultResource = isEditedFromBackoffice
		? JSON.parse(element.getAttribute(EnrichmentElementDataAttribute.defaultResource) as string)
		: resource;
	const deps = element.getAttribute(EnrichmentElementDataAttribute.deps) || undefined;

	return {
		page,
		id,
		type,
		resource: {
			...resource,
			deps,
		},
		defaultResource,
		importManagerId: `${id}-${page}`,
		isEditedFromBackoffice,
		isThemedItem: isAccountManagerOrAdmin && !!element.closest(`[class*=${constants.themedItemClassName}]`),
	};
};

export default (page: number, htmlDocument: Document, isAccountManagerOrAdmin: boolean): EnrichmentItemsData => {
	const items: EnrichmentConfig[] = [];
	let isError: boolean = false;

	const elements = ([
		...htmlDocument.querySelectorAll(`[class*=${constants.enrichmentClassName}]`),
	] as HTMLElement[]).filter(element => !hasLockedParent(element));

	try {
		elements.forEach(element => {
			const enrichmentData = getEnrichmentData(element);

			if (enrichmentData) {
				const { type, resource } = enrichmentData;
				const isSupportedEnrichment = Object.values(EnrichmentType).includes(type);

				if (isSupportedEnrichment) {
					items.push(getConfig(element, page, type, resource, isAccountManagerOrAdmin));
				}
			}
		});
	} catch (err) {
		isError = true;
	}

	return {
		items,
		isError,
	};
};



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Editor/utils/indesignEditor/editableEnrichments/getData.ts